import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { BRAND } from "@/lib/brand";

export const Route = createFileRoute("/install-check")({
  head: () => ({
    meta: [
      { title: `Install Check · ${BRAND.name}` },
      {
        name: "description",
        content: `Verify that the install prompt displays "${BRAND.name}" on Android Chrome, iOS Safari, iOS Chrome, and desktop Chrome/Edge.`,
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: `${BRAND.url}/install-check` }],
  }),
  component: InstallCheckPage,
});

type Platform = "android" | "ios" | "ios_chrome" | "desktop" | "desktop_edge" | "unknown";

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "android";
  const isIos = /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && "ontouchend" in document);
  if (isIos) {
    return /CriOS/i.test(ua) ? "ios_chrome" : "ios";
  }
  if (/Edg/i.test(ua)) return "desktop_edge";
  return "desktop";
}

interface Step {
  id: string;
  label: string;
}

const STEPS: Record<Exclude<Platform, "unknown">, { title: string; steps: Step[]; note?: string }> = {
  android: {
    title: "Android · Chrome",
    steps: [
      { id: "a1", label: "Open this site in Chrome (not in-app browser)." },
      { id: "a2", label: "Tap the ⋮ menu → Install app / Add to Home screen." },
      { id: "a3", label: `Confirm the prompt title reads exactly "${BRAND.name}".` },
      { id: "a4", label: `Install, then confirm the home-screen icon label reads "${BRAND.shortName}".` },
      { id: "a5", label: "Launch from the icon and verify the splash/title shows the same name." },
    ],
  },
  ios: {
    title: "iOS · Safari",
    steps: [
      { id: "i1", label: "Open this site in Safari (not Chrome / in-app browser)." },
      { id: "i2", label: "Tap the Share button → Add to Home Screen." },
      { id: "i3", label: `Confirm the suggested name reads "${BRAND.shortName}" before tapping Add.` },
      { id: "i4", label: "Open from the new home-screen icon and confirm the label below it matches." },
    ],
    note: "iOS caches the name at install. If a stale name appears, remove the old icon and re-add.",
  },
  ios_chrome: {
    title: "iOS · Chrome",
    steps: [
      { id: "ic1", label: "Open this site in the Chrome app (not an in-app browser)." },
      { id: "ic2", label: "Tap the ⋮ menu → Add to Home Screen." },
      { id: "ic3", label: `Confirm the suggested name reads "${BRAND.shortName}" before tapping Add.` },
      { id: "ic4", label: "Open from the new home-screen icon and confirm the label below it matches." },
    ],
    note: "iOS uses the same underlying WebKit engine. If a stale name appears, remove the old icon and re-add.",
  },
  desktop: {
    title: "Desktop · Chrome",
    steps: [
      { id: "d1", label: "Open this site in Chrome over HTTPS." },
      { id: "d2", label: "Click the install icon in the address bar (or ⋮ → Install / Add to Home Screen)." },
      { id: "d3", label: `Confirm the install dialog title reads "${BRAND.name}".` },
      { id: "d4", label: "Install, then verify the window title and taskbar/Dock label match." },
    ],
  },
  desktop_edge: {
    title: "Desktop · Edge",
    steps: [
      { id: "e1", label: "Open this site in Microsoft Edge over HTTPS." },
      { id: "e2", label: "Click the install icon in the address bar (or ⋮ → Apps → Install this site as an app)." },
      { id: "e3", label: `Confirm the install dialog title reads "${BRAND.name}".` },
      { id: "e4", label: "Install, then verify the window title and taskbar/Dock label match." },
    ],
  },
};

function InstallCheckPage() {
  const [platform, setPlatform] = useState<Platform>("unknown");
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [manifest, setManifest] = useState<{ name?: string; short_name?: string } | null>(null);

  useEffect(() => {
    setPlatform(detectPlatform());
    fetch("/manifest.webmanifest")
      .then((r) => r.json())
      .then(setManifest)
      .catch(() => setManifest(null));
  }, []);

  const toggle = (id: string) =>
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const nameOk = manifest?.name === BRAND.name;
  const shortOk = manifest?.short_name === BRAND.shortName;

  const sections = (Object.keys(STEPS) as Array<keyof typeof STEPS>);

  return (
    <main className="mx-auto max-w-2xl px-6 py-12 text-foreground">
      <h1 className="font-display text-3xl mb-2">Install Prompt Check</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Verify the install prompt and home-screen label read{" "}
        <strong className="text-foreground">{BRAND.name}</strong> on every device.
      </p>

      <section className="mb-8 rounded-lg border border-border p-4">
        <h2 className="font-medium mb-3">Manifest values (live)</h2>
        <dl className="text-sm space-y-1">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">name</dt>
            <dd className={nameOk ? "text-emerald-600" : "text-destructive"}>
              {manifest?.name ?? "…"} {nameOk ? "✓" : manifest ? "✗" : ""}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">short_name</dt>
            <dd className={shortOk ? "text-emerald-600" : "text-destructive"}>
              {manifest?.short_name ?? "…"} {shortOk ? "✓" : manifest ? "✗" : ""}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">expected</dt>
            <dd>{BRAND.name}</dd>
          </div>
        </dl>
      </section>

      {platform !== "unknown" && (
        <p className="text-xs text-muted-foreground mb-4">
          Detected device: <strong className="text-foreground">{STEPS[platform].title}</strong> — start here.
        </p>
      )}

      <div className="space-y-8">
        {sections.map((key) => {
          const section = STEPS[key];
          const isDetected = key === platform;
          return (
            <section
              key={key}
              className={`rounded-lg border p-4 ${
                isDetected ? "border-accent" : "border-border"
              }`}
            >
              <h2 className="font-medium mb-3">{section.title}</h2>
              <ol className="space-y-2">
                {section.steps.map((step, i) => (
                  <li key={step.id} className="flex items-start gap-3 text-sm">
                    <input
                      id={step.id}
                      type="checkbox"
                      checked={!!checked[step.id]}
                      onChange={() => toggle(step.id)}
                      className="mt-1"
                    />
                    <label htmlFor={step.id} className="cursor-pointer">
                      <span className="text-muted-foreground mr-1">{i + 1}.</span>
                      {step.label}
                    </label>
                  </li>
                ))}
              </ol>
              {section.note && (
                <p className="mt-3 text-xs text-muted-foreground">{section.note}</p>
              )}
            </section>
          );
        })}
      </div>

      <p className="mt-8 text-xs text-muted-foreground">
        If any prompt shows a different name, the device has cached an older manifest — uninstall, clear site
        data, and reinstall.
      </p>
    </main>
  );
}
